/**
 * Studio shell context + pure view-model selectors.
 *
 * The shell threads one `StudioContext` (the data surface, the active role/client, the
 * RBAC predicate, and the navigation callbacks) down to every screen. The shaping
 * helpers below are pure functions over the API reads, factored out so the screen
 * roll-ups (client sub-matrix, gap backlog ordering, audit relevance) are testable
 * without a renderer.
 */
import type { CoreStudioApi } from './data/api.js';
import type {
  AuditEntry,
  Client,
  CoverageEntry,
  Permission,
  RoleKey,
  StudioOutputType,
} from './data/types.js';
import { cellKey } from './data/coverage.js';

export type RuleMode = 'read' | 'author';

export interface StudioContext {
  api: CoreStudioApi;
  role: RoleKey;
  clientId: string;
  /** Current actor's display name (drives authoredBy / audit actor). */
  author: string;
  can: (perm: Permission) => boolean;
  currentClient: () => Client;
  bump: () => void;
  openRule: (id: string, mode?: RuleMode) => void;
  openSource: (id: string) => void;
  openDiff: (id: string) => void;
  openStore: (cell: string) => void;
  goView: (view: string) => void;
  createRule: (juris: string, domain: string) => void;
}

/** A coverage cell decorated with its jurisdiction + domain keys, for the client roll-up. */
export interface ClientCell extends CoverageEntry {
  key: string;
  jc: string;
  dk: string;
}

/** The cells a client touches (its jurisdictions × its domains), in matrix order. */
export function clientCells(api: CoreStudioApi, client: Client): ClientCell[] {
  const cells: ClientCell[] = [];
  for (const jc of client.jurisdictions) {
    for (const dk of client.domains) {
      const key = cellKey(jc, dk);
      const c = api.coverageCell(key);
      if (c) cells.push({ ...c, key, jc, dk });
    }
  }
  return cells;
}

/** Open gaps for a client (anything not fully covered), worst no-coverage rate first. */
export function gapsFor(cells: ClientCell[]): ClientCell[] {
  return cells
    .filter((c) => c.state !== 'covered')
    .sort((a, b) => b.noCoverage - a.noCoverage);
}

/** Audit entries that touch a client's jurisdictions/domains (rule- or source-linked). */
export function auditForClient(api: CoreStudioApi, client: Client, limit: number): AuditEntry[] {
  return api
    .audit()
    .filter((e) => {
      const r = api.rule(e.target);
      if (r) return client.domains.includes(r.domain) && client.jurisdictions.includes(r.juris);
      const s = api.source(e.target);
      if (s)
        return s.deps.some((dep) => {
          const dr = api.rule(dep);
          return dr ? client.domains.includes(dr.domain) : false;
        });
      return false;
    })
    .slice(0, limit);
}

/** Sources whose watch fired and that a client depends on (the "law to revisit" feed). */
export function watchForClient(api: CoreStudioApi, client: Client) {
  return api.sources().filter(
    (s) =>
      s.watch === 'changed' &&
      s.deps.some((dep) => {
        const dr = api.rule(dep);
        return dr ? client.domains.includes(dr.domain) : false;
      }),
  );
}

/** Human label for what a rule returns (read screens + editor). */
export const OUT_LABEL: Record<StudioOutputType, string> = {
  classification: 'classification',
  requirement: 'requirement',
  applicability: 'which regime',
  value: 'defined value',
  evidence: 'document required',
};

/** Authoring picker: how each output kind reads when choosing what a rule returns. */
export const OUTPUT_OPTIONS: { v: StudioOutputType; label: string }[] = [
  { v: 'classification', label: 'a classification (e.g. Class IIb)' },
  { v: 'requirement', label: 'a requirement (what is needed)' },
  { v: 'evidence', label: 'a required document (after a finding)' },
  { v: 'applicability', label: 'which regime applies' },
  { v: 'value', label: 'a defined value (e.g. a date)' },
];

/** Audit-kind → timeline dot colour. */
export const KIND_DOT: Record<AuditEntry['kind'], string> = {
  publish: 'var(--status-pass)',
  review: 'var(--status-info)',
  watch: 'var(--status-warn)',
  draft: 'var(--fg-3)',
  release: 'var(--garnet)',
  rollback: 'var(--status-fail)',
};

/** Audit-kind → badge class + icon. */
export const KIND_BADGE: Record<
  AuditEntry['kind'],
  { cls: string; icon: 'check' | 'eye' | 'file' | 'alert' | 'layers' | 'history' }
> = {
  publish: { cls: 'b-pass', icon: 'check' },
  review: { cls: 'b-info', icon: 'eye' },
  draft: { cls: 'b-neutral', icon: 'file' },
  watch: { cls: 'b-warn', icon: 'alert' },
  release: { cls: 'b-garnet', icon: 'layers' },
  rollback: { cls: 'b-fail', icon: 'history' },
};

/** Source kind → badge class (register table + detail). */
export const KIND_TAG: Record<string, string> = {
  regulation: 'b-garnet',
  directive: 'b-info',
  statute: 'b-neutral',
  annex: 'b-garnet',
  standard: 'b-pass',
  guidance: 'b-neutral',
  register: 'b-neutral',
};
